// Status badge component as specified in appmark.txt
import React from "react";

interface StatusBadgeProps {
  status:
    | "ready"
    | "working"
    | "waiting"
    | "error"
    | "running"
    | "paused"
    | "completed"
    | "failed"
    | "healthy"
    | "degraded"
    | "unhealthy"
    | "online"
    | "offline"
    | "slow"
    | "rate_limited"
    | "waiting_approval";
  size?: "sm" | "md" | "lg";
  showDot?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = "md",
  showDot = true,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case "ready":
      case "completed":
      case "healthy":
      case "online":
        return {
          color:
            "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
          dotColor: "bg-green-400",
          text: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "working":
      case "running":
        return {
          color:
            "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
          dotColor: "bg-blue-400",
          text: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "waiting":
      case "paused":
      case "waiting_approval":
      case "degraded":
      case "slow":
        return {
          color:
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
          dotColor: "bg-yellow-400",
          text: status === "waiting_approval" ? "Waiting Approval" : status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "error":
      case "failed":
      case "unhealthy":
      case "offline":
        return {
          color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
          dotColor: "bg-red-400",
          text: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "rate_limited":
        return {
          color:
            "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
          dotColor: "bg-orange-400",
          text: "Rate Limited",
        };
      default:
        return {
          color:
            "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
          dotColor: "bg-gray-400",
          text: status,
        };
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "px-2 py-1 text-xs";
      case "lg":
        return "px-3 py-2 text-sm";
      default:
        return "px-2.5 py-1.5 text-xs";
    }
  };

  const getDotSize = () => {
    switch (size) {
      case "sm":
        return "h-1.5 w-1.5";
      case "lg":
        return "h-2.5 w-2.5";
      default:
        return "h-2 w-2";
    }
  };

  const config = getStatusConfig();

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${
        config.color
      } ${getSizeClasses()}`}
    >
      {showDot && (
        <span
          className={`rounded-full ${config.dotColor} ${getDotSize()} mr-1.5`}
        />
      )}
      {config.text}
    </span>
  );
};
